import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../store";
import { fetchDashboardStats } from "../store/slices/dashboardSlice";
import { fetchSimCards } from "../store/slices/simCardSlice";
import { Card, Icon, PageHead, Sparkline, Table } from "../ui";
import { formatDate } from "../lib/format";
import { decodeTokenBrandSlug } from "../services/api";

// TODO(redesign): replace with /admin/stats/sparklines once backend adds it
const MOCK_USERS_SPARK = [12, 18, 15, 22, 28, 24, 33, 31, 38, 42, 40, 48, 55, 52, 61, 68, 72, 78, 82, 90];
const MOCK_SIMS_SPARK  = [88, 86, 85, 83, 80, 78, 76, 72, 70, 67, 64, 60, 58, 55, 52, 50, 47, 44, 42, 40];
const MOCK_SALES_SPARK = [3, 2, 4, 6, 5, 7, 4, 6, 9, 8, 5, 7, 10, 12, 8, 11, 9, 13, 10, 7];

type DeltaTone = "pos" | "neg" | "zero";
type Delta = { text: string; tone: DeltaTone };

function computeDelta(now: number, prev: number | null): Delta {
  if (prev === null || prev === 0) return { text: "—", tone: "zero" };
  const pct = ((now - prev) / prev) * 100;
  if (Math.abs(pct) < 0.05) return { text: "0%", tone: "zero" };
  const tone: DeltaTone = pct > 0 ? "pos" : "neg";
  const sign = pct > 0 ? "+" : "−";
  return { text: `${sign}${Math.abs(pct).toFixed(1)}%`, tone };
}

const StatTile: React.FC<{
  label: string;
  value: number;
  spark: number[];
  delta: Delta;
}> = ({ label, value, spark, delta }) => (
  <div className="stat-tile">
    <div className="top">
      <span className="label">{label}</span>
      <span
        className={
          delta.tone === "neg" ? "delta-neg" : delta.tone === "pos" ? "delta-pos" : ""
        }
        style={{ fontSize: 12, fontWeight: 500 }}
      >
        {delta.text}
      </span>
    </div>
    <div className="num">{value.toLocaleString()}</div>
    <Sparkline
      data={spark}
      color={delta.tone === "neg" ? "var(--danger)" : "var(--accent)"}
    />
  </div>
);

const InventoryRow: React.FC<{ label: string; value: number; dot: string }> = ({
  label,
  value,
  dot,
}) => (
  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
    <span style={{ width: 6, height: 6, borderRadius: 999, background: dot }} />
    <span style={{ color: "var(--muted)" }}>{label}</span>
    <span className="mono tnum" style={{ marginLeft: "auto", fontWeight: 500 }}>
      {value.toLocaleString()}
    </span>
  </div>
);

const PoolBar: React.FC<{ label: string; value: number; color: string }> = ({ label, value, color }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
    <span style={{ color: "var(--muted)", fontSize: 12.5 }}>{label}</span>
    <span
      style={{
        background: value > 0 ? color : "var(--danger)",
        color: "#fff",
        borderRadius: 4,
        padding: "2px 8px",
        fontSize: 12,
        fontWeight: 600,
        minWidth: 48,
        textAlign: "center",
      }}
    >
      {value.toLocaleString()}
    </span>
  </div>
);

const Dashboard: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { stats, loading, error } = useSelector((s: RootState) => s.dashboard);
  const simCards = useSelector((s: RootState) => s.simCards.simCards);

  useEffect(() => {
    dispatch(fetchDashboardStats());
    dispatch(fetchSimCards());
  }, [dispatch]);

  useEffect(() => {
    const onRefresh = () => {
      dispatch(fetchDashboardStats());
      dispatch(fetchSimCards());
    };
    window.addEventListener("admin:refresh", onRefresh);
    return () => window.removeEventListener("admin:refresh", onRefresh);
  }, [dispatch]);

  const deltas = useMemo(() => {
    const prevUsers = Number(localStorage.getItem("prevTotalUsers")) || null;
    const prevSims = Number(localStorage.getItem("prevAvailableSims")) || null;
    const prevSold = Number(localStorage.getItem("prevSoldToday")) || null;
    const d = {
      users: computeDelta(stats.total_users || 0, prevUsers),
      sims: computeDelta(stats.available_sims || 0, prevSims),
      sold: computeDelta(stats.sold_today || 0, prevSold),
    };
    if (stats.total_users != null) localStorage.setItem("prevTotalUsers", String(stats.total_users));
    if (stats.available_sims != null) localStorage.setItem("prevAvailableSims", String(stats.available_sims));
    if (stats.sold_today != null) localStorage.setItem("prevSoldToday", String(stats.sold_today));
    return d;
  }, [stats.total_users, stats.available_sims, stats.sold_today]);

  // TODO(redesign): replace with /admin/inventory once backend adds it.
  // SimCard has no user_id, so categories are derived from status + activation_time.
  const inventory = useMemo(() => {
    const active = simCards.filter((s) => s.status === 1).length;
    const inactive = simCards.filter(
      (s) => s.status === 0 && !!s.activation_time,
    ).length;
    const awaiting = simCards.filter(
      (s) => s.status === 0 && !s.activation_time,
    ).length;
    const total = active + inactive + awaiting || 1;
    return { active, inactive, awaiting, total };
  }, [simCards]);

  const poolNumbers: Array<{ country: string; count: number }> = useMemo(
    () => (stats.pool_numbers_available ?? []).map((r: any) => ({ country: r.country, count: Number(r.count) })),
    [stats.pool_numbers_available],
  );

  const poolEsims: number = stats.pool_esims_available ?? 0;

  // The "Shared eSIM Pool" banner shows global, unassigned stock and global
  // US/EU numbers — superadmin-only. WL admins should not see the shared pool.
  const authToken = typeof localStorage !== "undefined" ? localStorage.getItem("auth_token") : null;
  const isSuperAdmin = !(authToken ? decodeTokenBrandSlug(authToken) : null);

  if (loading === "pending" && !stats.total_users) {
    return (
      <div className="empty">
        <Icon name="refresh" size={20} />
        <p>Loading…</p>
      </div>
    );
  }
  if (error) {
    return (
      <Card pad>
        <div style={{ color: "var(--danger)" }}>{error}</div>
      </Card>
    );
  }

  return (
    <div>
      <PageHead title="Dashboard" sub="System overview and recent activity" />

      <div
        className="stats-grid"
        style={{ gap: 16, marginBottom: 16 }}
      >
        <StatTile
          label="Total users"
          value={stats.total_users ?? 0}
          spark={MOCK_USERS_SPARK}
          delta={deltas.users}
        />
        <StatTile
          label={stats.is_wl_admin ? "Total eSIMs sold" : "Available SIMs"}
          value={stats.available_sims ?? 0}
          spark={MOCK_SIMS_SPARK}
          delta={deltas.sims}
        />
        <StatTile
          label="Sold today"
          value={stats.sold_today ?? 0}
          spark={MOCK_SALES_SPARK}
          delta={deltas.sold}
        />
      </div>

      {/* Shared stock pool banner — superadmin only (global/unassigned stock) */}
      {isSuperAdmin && stats.pool_esims_available != null && (
        <div
          style={{
            marginBottom: 16,
            padding: "12px 16px",
            borderRadius: 8,
            background: poolEsims > 50 ? "var(--success-bg, #f0faf4)" : poolEsims > 0 ? "var(--warn-bg, #fffbeb)" : "var(--danger-bg, #fff1f1)",
            border: `1px solid ${poolEsims > 50 ? "var(--success, #22c55e)" : poolEsims > 0 ? "var(--warn, #f59e0b)" : "var(--danger, #ef4444)"}`,
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap" as const,
            gap: "12px 24px",
          }}
        >
          <span style={{ fontWeight: 600, fontSize: 13 }}>
            <Icon name="box" size={13} style={{ marginRight: 6, verticalAlign: "middle" }} />
            Shared eSIM Pool
          </span>
          <span style={{ fontSize: 13 }}>
            <span style={{ color: "var(--muted)" }}>Available eSIM cards: </span>
            <strong style={{ color: poolEsims > 0 ? "var(--success)" : "var(--danger)" }}>
              {poolEsims.toLocaleString()}
            </strong>
          </span>
          {poolNumbers.map((n) => (
            <span key={n.country} style={{ fontSize: 13 }}>
              <span style={{ color: "var(--muted)" }}>{n.country} numbers: </span>
              <strong style={{ color: n.count > 0 ? "var(--success)" : "var(--danger)" }}>
                {n.count.toLocaleString()}
              </strong>
            </span>
          ))}
          {poolEsims === 0 && (
            <span style={{ color: "var(--danger)", fontWeight: 600, fontSize: 12 }}>
              Stock depleted — contact superadmin to add inventory
            </span>
          )}
        </div>
      )}

      <div
        className="dashboard-grid"
        style={{ gap: 16, alignItems: "start" }}
      >
        <Card
          title="Last registered users"
          sub={`${stats.last_users?.length ?? 0} most recent`}
        >
          <Table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Email</th>
                <th style={{ textAlign: "right" }}>Created</th>
              </tr>
            </thead>
            <tbody>
              {(stats.last_users ?? []).slice(0, 8).map((u: any) => (
                <tr key={u.id}>
                  <td
                    className="num link"
                    onClick={() => navigate(`/users/${u.id}`)}
                  >
                    {u.id}
                  </td>
                  <td style={{ fontWeight: 500 }}>
                    {u.first_name} {u.last_name}
                  </td>
                  <td className="muted">{u.email}</td>
                  <td className="num" style={{ textAlign: "right" }}>
                    {formatDate(u.created)}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>

        {stats.is_wl_admin ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <Card title="Recent eSIM orders" sub="Latest orders for this brand">
              <Table>
                <thead>
                  <tr>
                    <th>ICCID</th>
                    <th>MSISDN</th>
                    <th style={{ textAlign: "right" }}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {(stats.last_orders ?? []).slice(0, 8).map((o: any) => (
                    <tr key={o.id}>
                      <td className="mono" style={{ fontSize: 11 }}>{o.iccid ?? "—"}</td>
                      <td className="muted">{o.issued_msisdn ?? o.original_msisdn ?? "—"}</td>
                      <td className="num" style={{ textAlign: "right" }}>{formatDate(o.issue_date_time || o.insert_date_time)}</td>
                    </tr>
                  ))}
                  {(stats.last_orders ?? []).length === 0 && (
                    <tr><td colSpan={3} className="muted" style={{ textAlign: "center", padding: 16 }}>No orders yet</td></tr>
                  )}
                </tbody>
              </Table>
            </Card>

            {/* Number ownership for this brand */}
            {(stats.brand_number_ownership ?? []).length > 0 && (
              <Card title="Number ownership" sub="Numbers assigned to your brand's users">
                <Table>
                  <thead>
                    <tr>
                      <th>Number (MSISDN)</th>
                      <th>Region</th>
                      <th>User</th>
                      <th style={{ textAlign: "right" }}>Assigned</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(stats.brand_number_ownership ?? []).slice(0, 10).map((n: any, i: number) => (
                      <tr key={i}>
                        <td className="mono" style={{ fontSize: 12 }}>{n.number}</td>
                        <td>{n.country}</td>
                        <td className="muted" style={{ fontSize: 12 }}>{n.email ?? `User #${n.user_id}`}</td>
                        <td className="num" style={{ textAlign: "right" }}>{formatDate(n.issued_time)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card>
            )}
          </div>
        ) : (
          <Card title="Inventory health" sub="SIM pool status">
            <div
              className="card-pad"
              style={{ display: "flex", flexDirection: "column", gap: 14 }}
            >
              <div className="segmented-bar">
                <span
                  style={{
                    background: "var(--success)",
                    width: `${(inventory.active / inventory.total) * 100}%`,
                  }}
                />
                <span
                  style={{
                    background: "var(--warn)",
                    width: `${(inventory.inactive / inventory.total) * 100}%`,
                  }}
                />
                <span
                  style={{
                    background: "var(--muted-2)",
                    width: `${(inventory.awaiting / inventory.total) * 100}%`,
                  }}
                />
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 12,
                  fontSize: 12.5,
                }}
              >
                <InventoryRow label="Active" value={inventory.active} dot="var(--success)" />
                <InventoryRow label="Inactive" value={inventory.inactive} dot="var(--warn)" />
                <InventoryRow label="Awaiting" value={inventory.awaiting} dot="var(--muted-2)" />
                <InventoryRow label="Total" value={inventory.total} dot="var(--ink)" />
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
